import type { BoxEventLevel, BoxStatus, Ec2Arch } from './types';

/** cuid2 ids are long; the first 10 characters are plenty to recognise a box. */
export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 10)}…` : id;
}

export function statusTone(status: BoxStatus): 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Ready':
      return 'success';
    case 'Updating':
      return 'warning';
    case 'Failed':
      return 'danger';
  }
}

export function ec2StateTone(state: string): 'success' | 'neutral' {
  return state === 'running' ? 'success' : 'neutral';
}

export function systemStatusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'ok':
      return 'success';
    case 'initializing':
      return 'warning';
    case 'impaired':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** A stack needs a Ready box whose EC2 system status check passes. */
export function canDeploy(status: BoxStatus, systemStatus: string | null | undefined): boolean {
  return status === 'Ready' && systemStatus === 'ok';
}

export function caddyFirst(stackIds: string[]): string[] {
  return [
    ...stackIds.filter((stackId) => stackId.toLowerCase() === 'caddy'),
    ...stackIds.filter((stackId) => stackId.toLowerCase() !== 'caddy'),
  ];
}

export function eventTone(level: BoxEventLevel): 'neutral' | 'danger' {
  return level === 'Error' ? 'danger' : 'neutral';
}

export function eventTime(rfc3339: string): string {
  const at = new Date(rfc3339);
  return Number.isNaN(at.getTime()) ? rfc3339 : at.toLocaleString();
}

/** Error messages are `format!("{err:?}")` of an anyhow chain
 *  (rust_code/box_jobs/src/deploy_stack.rs:52): the first line is the outermost
 *  context and the rest is the cause chain, useful but too long to show inline. */
export function splitEventMessage(msg: string): { summary: string; detail: string } {
  const trimmed = msg.trim();
  const breakAt = trimmed.indexOf('\n');
  if (breakAt === -1) return { summary: trimmed, detail: '' };
  return { summary: trimmed.slice(0, breakAt).trim(), detail: trimmed.slice(breakAt + 1).trim() };
}

export function archLabel(arch: Ec2Arch): string {
  return arch === 'Arm64' ? 'arm64' : 'x86_64';
}

/** The box image installs keys for the `ubuntu` user
 *  (rust_code/box_mng/src/ssh_assoc.rs:64-69). */
export function sshCommand(address: string): string {
  return `ssh ubuntu@${address}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
